// src/features/imports/model/imports.normalize.ts

import type {
  UploadedFileApiDto,
  ImportedFileDriverInfoApiDto,
  ImportedFileVehicleInfoApiDto,
} from './imports.api.dto'

import type {
  ImportsReadModel,
  ImportBucket,
  FileId,
  DriverImportId,
  VehicleImportId,
  DriverImportItem,
  VehicleImportItem,
} from './imports.state.types'

/**
 * Build a stable unique id for a driver row inside import logs.
 * We cannot use DB ids because "new"/"ignored" items don't have them.
 */
function makeDriverImportId(
  // fileId: FileId,
  // bucket: ImportBucket,
  d: ImportedFileDriverInfoApiDto,
  // index: number
): DriverImportId {
  const key = (d.cardNumber || d.fullName || 'unknown').trim()
  return `driver:${key}`
}

/**
 * Build a stable unique id for a vehicle row inside import logs.
 */
function makeVehicleImportId(
  // fileId: FileId,
  // bucket: ImportBucket,
  // v: ImportedFileVehicleInfoApiDto,
  registrationNumber: string,
  // index: number
): VehicleImportId {
  const key = (registrationNumber || 'unknown').trim()
  return `vehicle:${key}`
}

type DbStateApiDto = { key: string; isActive: boolean }

const toDbStateMap = (states: DbStateApiDto[]) =>
  Object.fromEntries(states.map((s) => [s.key, s.isActive]))

export function normalizeUploadedFiles(apiData: {
  files: UploadedFileApiDto[]
  driverDbStates?: DbStateApiDto[]
  vehicleDbStates?: DbStateApiDto[]
}): ImportsReadModel {
  const filesById: Record<FileId, UploadedFileApiDto> = {}

  const driverItemsById: Record<DriverImportId, DriverImportItem> = {}
  const vehicleItemsById: Record<VehicleImportId, VehicleImportItem> = {}

  const driverImportIdsByFileId: Record<FileId, DriverImportId[]> = {}
  const vehicleImportIdsByFileId: Record<FileId, VehicleImportId[]> = {}

  const customLogs: Record<FileId, string> = {}

  for (const file of apiData.files) {
    const fileId: FileId = file.id

    if(file.importedFileLogs.logs){
      customLogs[fileId] = file.importedFileLogs.logs
    }

    filesById[fileId] = file
    driverImportIdsByFileId[fileId] = []
    vehicleImportIdsByFileId[fileId] = []

    const logs = file.importedFileLogs

    const flatVehicles = [
      ...logs.existingVehicles,
      ...logs.newVehicles,
      ...logs.ignoredVehicles,
    ]

    const vehicleBuckets: Array<
      [ImportBucket, ImportedFileVehicleInfoApiDto[]]
    > = [
      ['existing', logs?.existingVehicles ?? []],
      ['new', logs?.newVehicles ?? []],
      ['ignored', logs?.ignoredVehicles ?? []],
    ]

    for (const [bucket, list] of vehicleBuckets) {
      list.forEach((v, index) => {
        const id = makeVehicleImportId(v.registrationNumber)

        vehicleItemsById[id] = {
          id,
          fileId,
          bucket,
          registrationNumber: v.registrationNumber,
          daysRead: v.daysRead,
        }

        vehicleImportIdsByFileId[fileId].push(id)
      })
    }

    const driverBuckets: Array<[ImportBucket, ImportedFileDriverInfoApiDto[]]> =
      [
        ['existing', logs?.existingDrivers ?? []],
        ['new', logs?.newDrivers ?? []],
        ['ignored', logs?.ignoredDrivers ?? []],
      ]

    for (const [bucket, list] of driverBuckets) {
      list.forEach((d, index) => {
        const id = makeDriverImportId(d)

        driverItemsById[id] = {
          id,
          fileId,
          bucket,
          fullName: d.fullName,
          cardNumber: d.cardNumber,
          daysRead: d.daysRead,
          activityDates: d.activityDates ?? [],
          cooperationStartDate: d.cooperationStartDate,
          vehiclesUsed:
            d.vehiclesUsed?.map((vu) => ({
              firstUse: vu.firstUse,
              lastUse: vu.lastUse,
              odometerBegin: vu.odometerBegin,
              odometerEnd: vu.odometerEnd,
              registrationNumber: vu.registrationNumber,
              id: makeVehicleImportId(vu.registrationNumber),
            })) ?? [],
        }

        driverImportIdsByFileId[fileId].push(id)
      })
    }
  }

  return {
    filesById,
    driverItemsById,
    vehicleItemsById,
    driverImportIdsByFileId,
    vehicleImportIdsByFileId,
    customLogs,
    dbStates:
      apiData.driverDbStates && apiData.vehicleDbStates
        ? {
            drivers: toDbStateMap(apiData.driverDbStates),
            vehicles: toDbStateMap(apiData.vehicleDbStates),
          }
        : undefined,
  }
}
